import { Card } from '@/components/Card/Index';
import AuthorSearchCard from '@/components/AuthorSearchCard/Index';
import Pagination from '@/components/Pagination/Index';
import SectionWrap from '@/components/SectionWrap/Index';
import { CloseIcon } from '@/icons/CloseIcon';
import { SearchIcon } from '@/icons/SearchIcon';
import SpinnerIcon from '@/icons/SpinnerIcon';
import { AUTHOR_POST_TYPE } from '@/constants';
import pulseConfig from '@/pulsex.config';
import { AsideSearchColumn } from '@/ui/AsideSearchColumn/Index';
import { useSearch } from '@pulsex/hooks';
import { ELASTIC_POSTS_PER_PAGE } from '@pulsex/services';
import { SearchScope } from '@pulsex/types';
import {
    buildElasticAuthorAwareSearchOptions,
    buildElasticAuthorLookupOptions,
    cn,
    pickExactAuthorMatches,
} from '@pulsex/utils';
import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

type Props = { isMobile?: any; types?: any };

/**
 * postType values that are not articles. The CMS search endpoint scoped results to a list of
 * section GUIDs, which mean nothing to SearchX, so exclude the non-article types instead.
 */
const NON_ARTICLE_POST_TYPES = ['banner', 'author', 'custom', 'statistics', 'comics'];

const SearchSuspense = ({ isMobile, types }: Props) => {
    const [orderBy, setOrderBy] = useState<0 | 1>(0);
    const [page, setPage] = useState(1);
    const [activeTypes, setActiveTypes] = useState<string[]>([]);

    const searchParams = useSearchParams();
    const searchValue = searchParams.get('q');
    const searchDateFrom = searchParams.get('From');
    const searchDateTo = searchParams.get('To');
    const searchAuthor = searchParams.get('Author');
    const searchCategory = searchParams.get('Category');
    const isAuthorOnlySearch = !!searchAuthor;
    const searchInTitle = searchParams.get('SearchInTitle') === 'true';
    const searchInText = searchParams.get('SearchInText') === 'true';

    // Ticking both, or neither, means "search everywhere" - the site's original behaviour.
    const searchScope: SearchScope =
        searchInTitle === searchInText ? 'all' : searchInTitle ? 'title' : 'text';

    useEffect(() => {
        if (types && typeof types === 'string') {
            const separatedTypes = types?.includes(',') ? types.split(',') : [types];
            const cleanedTypes = separatedTypes.filter((type: string) => type !== '');
            setActiveTypes(cleanedTypes);
        }
    }, [types]);

    const searchOptions = useMemo(
        () =>
            buildElasticAuthorAwareSearchOptions({
                q: searchValue || '',
                author: searchAuthor || undefined,
                authorPostType: AUTHOR_POST_TYPE,
                nonArticlePostTypes: NON_ARTICLE_POST_TYPES,
                orderBy,
                searchScope,
                from: searchDateFrom ? searchDateFrom + 'T00:00:00' : undefined,
                to: searchDateTo ? searchDateTo + 'T00:00:00' : undefined,
                categories: searchCategory || undefined,
            }),
        [
            searchValue,
            searchAuthor,
            orderBy,
            searchScope,
            searchDateFrom,
            searchDateTo,
            searchCategory,
        ]
    );

    const authorLookupOptions = useMemo(
        () =>
            buildElasticAuthorLookupOptions({
                q: searchValue || '',
                authorPostType: AUTHOR_POST_TYPE,
                orderBy,
            }),
        [searchValue, orderBy]
    );

    const { data, isLoading } = useSearch(pulseConfig, {
        ...searchOptions,
        page,
    });

    const { data: authorLookupData, isLoading: isAuthorLookupLoading } = useSearch(
        pulseConfig,
        {
            ...authorLookupOptions,
            page: 1,
            enabled: !isAuthorOnlySearch && !!searchValue,
        }
    );

    const authorResults = useMemo(() => {
        if (isAuthorOnlySearch) {
            const posts = data?.posts || [];
            return searchAuthor ? pickExactAuthorMatches(posts, searchAuthor) : posts;
        }
        return authorLookupData?.posts || [];
    }, [isAuthorOnlySearch, data?.posts, searchAuthor, authorLookupData?.posts]);

    const postResults = useMemo(() => {
        if (isAuthorOnlySearch) return [];
        return data?.posts || [];
    }, [isAuthorOnlySearch, data?.posts]);

    const numberOfPages = Math.ceil((data?.count || 0) / ELASTIC_POSTS_PER_PAGE);
    const handleNext = () => {
        if (page === numberOfPages) return;
        setPage(page + 1);
    };
    const handlePrev = () => {
        if (page === 1) return;
        setPage(page - 1);
    };

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [page, orderBy, activeTypes]);

    useEffect(() => {
        setPage(1);
    }, [searchDateFrom, searchDateTo, searchAuthor, searchCategory, searchValue, searchScope]);

    const isLoadingResults =
        isLoading || (!isAuthorOnlySearch && isAuthorLookupLoading);
    const hasNoResults =
        !searchValue ||
        (!isLoadingResults && authorResults.length === 0 && postResults.length === 0);

    return (
        <>
            <SectionWrap className="" innerClassName="max-lg:flex-row max-md:flex-col">
                <AsideSearchColumn
                    data={undefined}
                    template="search"
                    className="max-md:w-full lg:w-1/6 lg:ml-3"
                />
                <div className="flex-col space-y-4 flex w-full lg:w-5/6 max-md:mt-4">
                    <div
                        className={cn(
                            'w-fit text-[#242424] font-semibold pb-2 border-b-2 border-masterRed',
                            'text-4xl md:text-5xl'
                        )}
                    >
                        نتائج البحث
                    </div>
                    {searchValue && isLoadingResults ? (
                        <div
                            role="status"
                            className=" w-full h-[50vh] flex items-center justify-center"
                        >
                            <SpinnerIcon className={'w-14 h-14 m-auto fill-masterRed'} />
                        </div>
                    ) : (
                        ''
                    )}
                    {hasNoResults ? (
                        <div className="grid place-items-center">
                            <div className="relative my-2">
                                <div className="absolute top-0 ml-3 left-1/2 -translate-x-1/2 bg-red-600 p-1 rounded-full">
                                    <CloseIcon size={30} className="fill-white" />
                                </div>
                                <SearchIcon size={185} className="fill-gray-800" />
                            </div>

                            <p className="text-[#242424] mx-auto text2xl md:text-3xl lg:text-4xl">
                                لم يتم العثور على نتائج
                            </p>
                        </div>
                    ) : (
                        ''
                    )}

                    {authorResults.length > 0 && (
                        <div
                            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-md:pt-4 pt-6"
                            dir={pulseConfig.LANGUAGE === 'ar' ? 'rtl' : 'ltr'}
                        >
                            {authorResults.map((item: any, index: number) => (
                                <AuthorSearchCard
                                    key={item?.id || `author-${index}`}
                                    data={item}
                                    className="my-2"
                                />
                            ))}
                        </div>
                    )}

                    {postResults.length > 0 && (
                        <div
                            className="grid grid-cols-2 lg:grid-cols-3 max-md:pt-4 pt-6"
                            dir={pulseConfig.LANGUAGE === 'ar' ? 'rtl' : 'ltr'}
                        >
                            {postResults.map((item: any, index: number) => (
                                <Card
                                    key={item?.id || index}
                                    data={item}
                                    className={cn(
                                        'my-4 lg:mx-3 flex flex-col space-y-1',
                                        'max-lg:ml-0 max-lg:mr-3 max-lg:my-3  max-md:my-1.5  max-md:mr-1.5 max-md:ml-0',
                                        index % 2 === 0 &&
                                            'max-lg:mr-0 max-lg:ml-3 max-lg:my-3 max-md:my-1.5 max-md:ml-1.5 max-md:mr-0'
                                    )}
                                    customTitle="line-clamp-2"
                                    customSummary="line-clamp-4"
                                    isMobile={isMobile}
                                    showCategory={false}
                                    isSearch={true}
                                    template="default"
                                />
                            ))}
                        </div>
                    )}
                    {numberOfPages > 1 && (
                        <Pagination
                            currentPage={page}
                            setPage={setPage}
                            numberOfPages={numberOfPages}
                            handlePrev={handlePrev}
                            handleNext={handleNext}
                        />
                    )}
                </div>
            </SectionWrap>
        </>
    );
};

export default SearchSuspense;
